import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Check, Building2, Store, Network, MapPin,
  Trash2, Plus, Loader2, Sparkles, ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d] = { open: '07:00', close: '17:00' };
  return acc;
}, {});

const WAITLIST_POSES = [
  { key: 'epos_now', name: 'EPOS Now' },
  { key: 'zettle', name: 'Zettle' },
  { key: 'sumup', name: 'SumUp' },
  { key: 'lightspeed', name: 'Lightspeed' },
  { key: 'clover', name: 'Clover' },
  { key: 'revel', name: 'Revel' },
];

const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', tz: 'Europe/London' },
  { code: 'US', name: 'United States', tz: 'America/New_York' },
  { code: 'IE', name: 'Ireland', tz: 'Europe/Dublin' },
  { code: 'AU', name: 'Australia', tz: 'Australia/Sydney' },
  { code: 'CA', name: 'Canada', tz: 'America/Toronto' },
];

const Steps = ['shape', 'location', 'pos', 'finish'];

const StepDots = ({ step }) => {
  const idx = Steps.indexOf(step);
  return (
    <div className="flex items-center justify-center gap-2 mb-8" data-testid="onboarding-steps">
      {Steps.map((s, i) => (
        <div
          key={s}
          className={`h-2 rounded-full transition-all ${
            i === idx ? 'w-8 bg-primary' : i < idx ? 'w-2 bg-primary/60' : 'w-2 bg-muted'
          }`}
        />
      ))}
    </div>
  );
};

const OnboardingPage = () => {
  const { user, token, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState('loading'); // loading | gate | signup | wizard
  const [step, setStep] = useState('shape');
  const [code, setCode] = useState('');
  const [defaultsPreview, setDefaultsPreview] = useState(null);
  const [enabledModules, setEnabledModules] = useState([]);
  const [planLabel, setPlanLabel] = useState(null);
  const [locations, setLocations] = useState([]);

  // Gate form state
  const [gateError, setGateError] = useState('');
  const [gateLoading, setGateLoading] = useState(false);

  // Signup state
  const [sName, setSName] = useState('');
  const [sEmail, setSEmail] = useState('');
  const [sPwd, setSPwd] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);

  // Wizard state
  const [shape, setShape] = useState('single');
  const [draftLocations, setDraftLocations] = useState([
    { name: '', address: '', country: 'GB', timezone: 'Europe/London', opening_hours: DEFAULT_HOURS },
  ]);
  const [locPos, setLocPos] = useState({}); // location_id → pos_provider
  const [savingLocs, setSavingLocs] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Load state on mount: if user is logged in, decide the resume step; else show gate
  useEffect(() => {
    (async () => {
      if (!token) {
        setPhase('gate');
        return;
      }
      try {
        const res = await api.get('/onboarding/state');
        if (res.data.completed) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setStep(res.data.state?.step || 'shape');
        setShape(res.data.state?.business_shape || 'single');
        setEnabledModules(res.data.enabled_modules || []);
        setPlanLabel(res.data.plan_label);
        setLocations(res.data.locations || []);
        setPhase('wizard');
      } catch (e) {
        // Logged in but no onboarding state → send to dashboard
        navigate('/dashboard', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Handle Square OAuth return
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sq = params.get('square');
    if (sq === 'connected') {
      toast.success('Square connected');
      // Refresh locations to pick up the connection
      if (token) api.get('/onboarding/state').then(r => setLocations(r.data.locations || []));
    } else if (sq === 'denied' || sq === 'error') {
      toast.error(`Square ${sq}: ${params.get('detail') || ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyCode = async (e) => {
    e.preventDefault();
    setGateError('');
    setGateLoading(true);
    try {
      const res = await axios.post(`${API}/onboarding/verify-code`, { code });
      setDefaultsPreview(res.data.defaults);
      setPhase('signup');
    } catch (err) {
      setGateError(err.response?.data?.detail || 'This invite code isn\'t valid.');
    } finally {
      setGateLoading(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    setSignupLoading(true);
    try {
      const res = await axios.post(`${API}/onboarding/signup`, {
        code, name: sName, email: sEmail, password: sPwd,
      });
      localStorage.setItem('token', res.data.access_token);
      // Full reload keeps AuthContext/ModulesContext simple — they'll pick up the new token
      window.location.assign('/onboarding');
    } catch (err) {
      setSignupError(err.response?.data?.detail || 'Could not create account');
    } finally {
      setSignupLoading(false);
    }
  };

  const persistStep = async (next) => {
    try {
      await api.put('/onboarding/state', { step: next });
    } catch { /* non-fatal */ }
    setStep(next);
  };

  // ── Screen 1: Shape ───
  const chooseShape = async (s) => {
    setShape(s);
    try { await api.put('/onboarding/state', { business_shape: s, step: 'location' }); } catch {}
    setStep('location');
  };

  // ── Screen 2: Locations ───
  const addDraft = () => {
    if (draftLocations.length + locations.length >= 5) {
      toast.error('Up to 5 locations in onboarding — add more later from Settings.');
      return;
    }
    setDraftLocations([
      ...draftLocations,
      { name: '', address: '', country: 'GB', timezone: 'Europe/London', opening_hours: DEFAULT_HOURS },
    ]);
  };
  const removeDraft = (i) => {
    setDraftLocations(draftLocations.filter((_, idx) => idx !== i));
  };
  const updateDraft = (i, patch) => {
    const next = [...draftLocations];
    next[i] = { ...next[i], ...patch };
    if (patch.country) {
      const c = COUNTRIES.find(c => c.code === patch.country);
      if (c) next[i].timezone = c.tz;
    }
    setDraftLocations(next);
  };
  const updateDraftHours = (i, day, field, value) => {
    const next = [...draftLocations];
    next[i].opening_hours = {
      ...next[i].opening_hours,
      [day]: { ...(next[i].opening_hours?.[day] || {}), [field]: value },
    };
    setDraftLocations(next);
  };

  const saveLocationsAndNext = async () => {
    const valid = draftLocations.filter(l => l.name.trim() && l.address.trim());
    if (!valid.length) {
      toast.error('Give us at least one location name and address.');
      return;
    }
    setSavingLocs(true);
    try {
      for (const l of valid) {
        await api.post('/onboarding/add-location', l);
      }
      const res = await api.get('/onboarding/state');
      setLocations(res.data.locations || []);
      setDraftLocations([]);
      await persistStep('pos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save locations');
    } finally {
      setSavingLocs(false);
    }
  };

  // ── Screen 3: POS per location ───
  const connectSquare = async (locationId) => {
    try {
      const res = await api.post('/square/connect', { location_id: locationId });
      window.location.assign(res.data.authorization_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start Square connect');
    }
  };
  const choosePos = (locationId, pos) => {
    setLocPos({ ...locPos, [locationId]: pos });
    if (WAITLIST_POSES.find(w => w.key === pos)) {
      api.post('/onboarding/pos-waitlist', { pos_name: pos, location_id: locationId }).catch(() => {});
      toast.success('Added to the waitlist — we\'ll email you when it\'s ready');
    }
  };

  const finishWizard = async () => {
    setCompleting(true);
    try {
      await api.post('/onboarding/complete');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not finish onboarding');
    } finally {
      setCompleting(false);
    }
  };

  // ── Render ───
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f2b2b]">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div className="min-h-screen bg-[#0f2b2b] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-white tracking-tight">Tillytics</h1>
            <p className="text-teal-300/70 mt-3">Enter your invite code to begin.</p>
          </div>
          <form onSubmit={verifyCode} className="space-y-4">
            <Input
              autoFocus
              data-testid="invite-code-input"
              placeholder="Invite code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="h-14 text-center text-xl tracking-[0.3em] font-mono bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-teal-400"
            />
            {gateError && <p className="text-sm text-rose-300 text-center" data-testid="invite-error">{gateError}</p>}
            <Button
              type="submit"
              disabled={gateLoading || !code.trim()}
              className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"
              data-testid="verify-code-btn"
            >
              {gateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
            </Button>
          </form>
          <p className="text-xs text-teal-400/40 text-center mt-8">
            Already have an account? <a href="/" className="underline hover:text-teal-300">Sign in</a>
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'signup') {
    return (
      <div className="min-h-screen bg-[#0f2b2b] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/40 mb-3" data-testid="invite-accepted-badge">
              <Check className="w-3 h-3 mr-1" /> Invite accepted
            </Badge>
            <h1 className="text-3xl font-bold text-white">Create your account</h1>
            {defaultsPreview?.notes && (
              <p className="text-teal-300/70 mt-3 text-sm italic">"{defaultsPreview.notes}"</p>
            )}
            {defaultsPreview?.plan_label && (
              <p className="text-teal-400/50 mt-2 text-xs">{defaultsPreview.plan_label}</p>
            )}
          </div>
          <form onSubmit={submitSignup} className="space-y-3">
            <Input
              data-testid="signup-name-input"
              placeholder="Your name"
              value={sName} onChange={(e) => setSName(e.target.value)} required
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <Input
              data-testid="signup-email-input"
              type="email" placeholder="Email"
              value={sEmail} onChange={(e) => setSEmail(e.target.value)} required
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <Input
              data-testid="signup-password-input"
              type="password" placeholder="Password (8+ chars)"
              value={sPwd} onChange={(e) => setSPwd(e.target.value)} required minLength={8}
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            {signupError && <p className="text-sm text-rose-300" data-testid="signup-error">{signupError}</p>}
            <Button
              type="submit"
              disabled={signupLoading}
              className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"
              data-testid="signup-submit-btn"
            >
              {signupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create account <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Wizard phase
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <div>
              <div className="text-sm text-muted-foreground">Welcome, {user?.name?.split(' ')[0]}</div>
              <div className="text-lg font-semibold">Set up Tillytics</div>
            </div>
          </div>
          {planLabel && <Badge variant="outline" data-testid="plan-label">{planLabel}</Badge>}
        </div>
        <StepDots step={step} />

        {step === 'shape' && (
          <div className="space-y-4" data-testid="step-shape">
            <h2 className="text-2xl font-bold text-center mb-2">What's the shape of your business?</h2>
            <p className="text-muted-foreground text-center mb-8">You can change this later in Settings.</p>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { k: 'single', title: 'Single site', desc: 'One café or restaurant.', icon: Store },
                { k: 'multi', title: 'Multi-site', desc: 'Two to five locations.', icon: Building2 },
                { k: 'franchise', title: 'Franchise HQ', desc: 'HQ coordinating franchisees.', icon: Network },
              ].map(({ k, title, desc, icon: Icon }) => (
                <Card
                  key={k}
                  onClick={() => chooseShape(k)}
                  className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
                  data-testid={`shape-${k}`}
                >
                  <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                    <Icon className="w-10 h-10 text-primary" />
                    <h3 className="font-semibold text-lg">{title}</h3>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {step === 'location' && (
          <div className="space-y-6" data-testid="step-location">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Your first location</h2>
              <p className="text-muted-foreground">We'll use this to tailor currency, timezone and tax.</p>
            </div>
            {shape === 'franchise' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                We'll finish franchise setup after onboarding — for now add your first site.
              </div>
            )}
            {draftLocations.map((loc, i) => (
              <Card key={i} data-testid={`location-draft-${i}`}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" /> Location {i + 1}</h3>
                    {draftLocations.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeDraft(i)} data-testid={`remove-location-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Business name</Label>
                      <Input
                        data-testid={`location-name-${i}`}
                        value={loc.name}
                        onChange={(e) => updateDraft(i, { name: e.target.value })}
                        placeholder="Priya's Café"
                      />
                    </div>
                    <div>
                      <Label>Trading address</Label>
                      <Input
                        data-testid={`location-address-${i}`}
                        value={loc.address}
                        onChange={(e) => updateDraft(i, { address: e.target.value })}
                        placeholder="14 Harrow Rd, London"
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Select value={loc.country} onValueChange={(v) => updateDraft(i, { country: v })}>
                        <SelectTrigger data-testid={`location-country-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(c => (
                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Timezone</Label>
                      <Input value={loc.timezone} onChange={(e) => updateDraft(i, { timezone: e.target.value })} />
                    </div>
                  </div>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Opening hours (optional)</summary>
                    <div className="mt-3 space-y-2">
                      {DAYS.map(day => (
                        <div key={day} className="flex items-center gap-2">
                          <span className="w-24 capitalize text-sm text-muted-foreground">{day}</span>
                          <Input
                            type="time" className="w-32"
                            value={loc.opening_hours?.[day]?.open || ''}
                            onChange={(e) => updateDraftHours(i, day, 'open', e.target.value)}
                          />
                          <span className="text-muted-foreground">–</span>
                          <Input
                            type="time" className="w-32"
                            value={loc.opening_hours?.[day]?.close || ''}
                            onChange={(e) => updateDraftHours(i, day, 'close', e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </details>
                </CardContent>
              </Card>
            ))}
            {(shape === 'multi' || shape === 'franchise') && draftLocations.length + locations.length < 5 && (
              <Button variant="outline" onClick={addDraft} className="w-full" data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-2" /> Add another location
              </Button>
            )}
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => persistStep('shape')} data-testid="back-shape">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={saveLocationsAndNext} disabled={savingLocs} data-testid="save-locations-btn">
                {savingLocs ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Next <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        )}

        {step === 'pos' && (
          <div className="space-y-6" data-testid="step-pos">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Connect your POS</h2>
              <p className="text-muted-foreground">Pick one source per location. You can change this later.</p>
            </div>
            {locations.map(loc => (
              <Card key={loc.id} data-testid={`pos-location-${loc.id}`}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">{loc.name}</h3>
                      <p className="text-xs text-muted-foreground">{loc.address}</p>
                    </div>
                    {loc.pos_provider === 'square' && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`pos-connected-${loc.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Square connected
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Button
                      variant={loc.pos_provider === 'square' ? 'default' : 'outline'}
                      onClick={() => connectSquare(loc.id)}
                      data-testid={`connect-square-${loc.id}`}
                      className="justify-start"
                    >
                      <div className="w-4 h-4 rounded bg-[#006AFF] mr-2" />
                      Connect Square
                    </Button>
                    <Button
                      variant={locPos[loc.id] === 'toast' ? 'default' : 'outline'}
                      onClick={() => choosePos(loc.id, 'toast')}
                      data-testid={`pick-toast-${loc.id}`}
                      className="justify-start"
                    >
                      <div className="w-4 h-4 rounded bg-[#FF4C00] mr-2" />
                      Toast (CSV)
                    </Button>
                    <Button
                      variant={locPos[loc.id] === 'manual' ? 'default' : 'outline'}
                      onClick={() => choosePos(loc.id, 'manual')}
                      data-testid={`pick-manual-${loc.id}`}
                      className="justify-start"
                    >
                      Manual revenue
                    </Button>
                    <Select onValueChange={(v) => choosePos(loc.id, v)} value={locPos[loc.id] && WAITLIST_POSES.find(w => w.key === locPos[loc.id])?.key || ''}>
                      <SelectTrigger data-testid={`other-pos-${loc.id}`}>
                        <SelectValue placeholder="Other POS…" />
                      </SelectTrigger>
                      <SelectContent>
                        {WAITLIST_POSES.map(w => (
                          <SelectItem key={w.key} value={w.key}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(locPos[loc.id] === 'toast' || WAITLIST_POSES.find(w => w.key === locPos[loc.id])) && (
                    <div className="bg-muted/40 rounded-lg p-3 text-sm flex items-start gap-2">
                      <ExternalLink className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">Upload CSVs any time from Imports.</p>
                        <p className="text-muted-foreground text-xs mt-1">
                          Live sync is coming for this POS — until then export CSVs from your till and drop them in.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => persistStep('location')} data-testid="back-location">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => persistStep('finish')} data-testid="next-finish">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 'finish' && (
          <div className="space-y-6" data-testid="step-finish">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">You're ready to go</h2>
              <p className="text-muted-foreground">Here's what we've set up for you.</p>
            </div>
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="text-sm text-muted-foreground">Business shape</div>
                  <div className="font-medium capitalize">{shape.replace('_', ' ')}</div>
                </div>
                <div className="flex justify-between items-start">
                  <div className="text-sm text-muted-foreground">Locations</div>
                  <div className="text-right">
                    {locations.map(l => (
                      <div key={l.id} className="font-medium">{l.name} <span className="text-xs text-muted-foreground">({l.pos_provider})</span></div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-start">
                  <div className="text-sm text-muted-foreground">Modules enabled</div>
                  <div className="flex flex-wrap gap-1 justify-end max-w-md">
                    {enabledModules.map(m => (
                      <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => persistStep('pos')} data-testid="back-pos">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={finishWizard}
                disabled={completing}
                data-testid="open-dashboard-btn"
                size="lg"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Open my dashboard <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
